import React, { useEffect, useState } from "react";
import { Flex, HStack, Input, Menu, Button, IconButton, Text } from "@commonsku/design-system";
import { LuCheck, LuChevronDown, LuX } from "react-icons/lu";
import type { Conversation } from "../../types/skubot";
import { DeleteConversationModal } from "./DeleteConversationModal";

export type ChatHeaderProps = Readonly<{
  conversation: Conversation
  onRenameConversation: (conversationId: string, title: string) => void
  onDeleteConversation: (conversationId: string) => void
}>

export function ChatHeader(props: ChatHeaderProps) {
  const { conversation, onRenameConversation, onDeleteConversation } = props;

  const [isEditing, setIsEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(conversation.title);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    setIsEditing(false);
    setDraftTitle(conversation.title);
    setDeleteDialogOpen(false);
  }, [conversation.sessionId]);

  useEffect(() => {
    if (!isEditing) {
      setDraftTitle(conversation.title);
    }
  }, [conversation.title, isEditing]);

  function startRename() {
    setDraftTitle(conversation.title);
    setIsEditing(true);
  }

  function cancelRename() {
    setDraftTitle(conversation.title);
    setIsEditing(false);
  }

  function saveRename() {
    const trimmedTitle = draftTitle.trim();

    if (!trimmedTitle) return;

    onRenameConversation(conversation.sessionId, trimmedTitle);
    setIsEditing(false);
  }

  function confirmDelete() {
    setDeleteDialogOpen(false);
    onDeleteConversation(conversation.sessionId);
  }

  return (
    <Flex
      h="56px"
      flexShrink={0}
      align="center"
      px="16px"
      bg="white"
      borderBottom="1px solid"
      borderColor="border.muted"
    >
      {isEditing ? (
        <Flex align="center" gap="4px" w="100%">
          <Input
            value={draftTitle}
            autoFocus
            size="xs"
            rounded="md"
            fontSize="14px"
            // colorPalette="brand"
            maxW={{ base: "220px", md: "360px" }}
            border="1px solid var(--chakra-colors-brand-focus-ring)"
            boxShadow="0 0 0 2px var(--chakra-colors-brand-subtle)"
            outline="none"
            _focus={{ borderColor: "brand.focusRing", boxShadow: "0 0 0 2px var(--chakra-colors-brand-subtle)" }}
            _focusVisible={{ outline: "none", borderColor: "brand.focusRing", boxShadow: "0 0 0 2px var(--chakra-colors-brand-subtle)" }}
            onChange={(event) => setDraftTitle(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                saveRename();
              }

              if (event.key === "Escape") {
                event.preventDefault();
                cancelRename();
              }
            }}
          />

          <IconButton
            label="Save chat title"
            size="xs"
            variant="ghost"
            rounded="sm"
            color="success.solid"
            _hover={{ color: "success.solid", bg: "success.subtle" }}
            _active={{ color: "success.solid", bg: "success.subtle" }}
            disabled={!draftTitle.trim()}
            onClick={saveRename}
          >
            <LuCheck size={16} />
          </IconButton>

          <IconButton
            label="Cancel rename"
            size="xs"
            variant="ghost"
            rounded="sm"
            color="critical.solid"
            _hover={{ color: "critical.solid", bg: "critical.subtle" }}
            _active={{ color: "critical.solid", bg: "critical.subtle" }}
            onClick={cancelRename}
          >
            <LuX size={16} />
          </IconButton>
        </Flex>
      ) : (
        <Menu.Root
          positioning={{ placement: "bottom-start", strategy: "fixed" }}
          onSelect={(details) => {
            if (details.value === "rename") {
              startRename();
            }

            if (details.value === "delete") {
              setDeleteDialogOpen(true);
            }
          }}
        >
          {/* @ts-ignore */}
          <Menu.Trigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              h="32px"
              px="2"
              borderRadius="md"
              _hover={{ bg: "gray.100" }}
              _open={{ bg: "brand.muted" }}
            >
              <HStack gap="1" minW={0}>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  maxW={{ base: "220px", md: "360px" }}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  margin="0"
                  padding="0"
                >
                  {conversation.title}
                </Text>

                <LuChevronDown size={15} />
              </HStack>
            </Button>
          </Menu.Trigger>

          {/* @ts-ignore */}
          <Menu.Positioner>
            {/* @ts-ignore */}
            <Menu.Content minW="148px" p="8px" boxShadow="0 0 1px 0 rgba(24, 24, 27, 0.30), 0 8px 16px 0 rgba(24, 24, 27, 0.10)">
              {/* @ts-ignore */}
              <Menu.Item value="rename" h="32px" alignItems="center">
                <HStack align="center" px="10px" py="2px" rounded="md" fontSize="13px">
                  <Text fontSize="sm" m="0" fontFamily="body">Rename</Text>
                </HStack>
              </Menu.Item>

              {/* @ts-ignore */}
              <Menu.Item
                value="delete"
                h="32px"
                alignItems="center"
                color="red.700"
                _highlighted={{ bg: "red.50", color: "red.800" }}
              >
                <HStack align="center" px="10px" py="2px" rounded="md" fontSize="13px">
                  <Text fontSize="sm" m="0" fontFamily="body">Delete</Text>
                </HStack>
              </Menu.Item>
            </Menu.Content>
          </Menu.Positioner>
        </Menu.Root>
      )}

      <DeleteConversationModal
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={confirmDelete}
      />
    </Flex>
  );
}
